// Editable list of an athlete's college visits, for the internal Concierge
// page. Admin/staff act on the athlete via ?as_athlete_id={userId}; the same
// GET/POST/DELETE /athletes/visits endpoints back the athlete's own editor
// (OffersPage) and the iOS VisitEditorSheet. College names resolve to unitid
// via the static /data/colleges.json (client-side match). Styled as the
// concierge redesign's table ledger (ConciergePage.css .cn-v*).
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import CollegeLogo from './CollegeLogo';
import CollegePicker from './CollegePicker';
import { groupVisits, visitRelative } from '../lib/conciergeSchoolsModel';
import { utcTodayIso } from '../lib/conciergeTodoModel';

const MAX_VISITS = 20;

const VISIT_TYPES = [
  { v: 'unofficial', l: 'Unofficial' },
  { v: 'official', l: 'Official' },
];

const VISIT_FORMATS = [
  { v: 'game_day', l: 'Game Day' },
  { v: 'junior_day', l: 'Junior Day' },
  { v: 'midweek', l: 'Midweek' },
  { v: 'prospect_camp', l: 'Prospect Camp' },
  { v: 'overnight', l: 'Overnight' },
  { v: 'virtual', l: 'Virtual' },
];

const emptyVisit = (slot) => ({
  slot_number: slot, unitid: null, college_name: '', visit_date: '',
  visit_end_date: '', visit_type: 'unofficial', visit_format: 'game_day',
});

export default function ConciergeVisitsEditor({ userId, onSaved, onVisitsChange, targetUnitids = [] }) {
  const { API_URL } = useAuth();
  const cfg = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    params: { as_athlete_id: userId },
  });

  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [queryBySlot, setQueryBySlot] = useState({});
  // Row grouping/order is a snapshot of slot numbers, refreshed only on load,
  // after a successful save, add and delete -- never on keystrokes, so a row
  // under the cursor neither re-sorts nor remounts mid-edit.
  const [order, setOrder] = useState({ upcoming: [], past: [] });
  const visitsRef = useRef([]);
  visitsRef.current = visits;
  const snapshot = (list) => {
    const g = groupVisits(list, utcTodayIso());
    setOrder({ upcoming: g.upcoming.map(v => v.slot_number), past: g.past.map(v => v.slot_number) });
  };

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/athletes/visits`, cfg())
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        setVisits(data);
        snapshot(data);
        setQueryBySlot(Object.fromEntries(data.map(v => [v.slot_number, v.college_name || ''])));
      })
      .catch(err => setError(err.response?.data?.detail || 'Failed to load visits.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [userId, API_URL]);

  useEffect(() => { if (onVisitsChange) onVisitsChange(visits); }, [visits, onVisitsChange]);

  const persist = (visit) => {
    if (!visit.college_name) return;
    axios.post(`${API_URL}/athletes/visits`, visit, cfg())
      .then(res => {
        const merged = visitsRef.current.map(v => v.slot_number === visit.slot_number ? { ...v, ...res.data } : v);
        setVisits(merged);
        snapshot(merged);
        if (onSaved) onSaved();
      })
      .catch(err => setError(err.response?.data?.detail || 'Failed to save visit.'));
  };

  const updateField = (slot, patch, save = false) => {
    setVisits(prev => {
      const next = prev.map(v => v.slot_number === slot ? { ...v, ...patch } : v);
      if (save) { const v = next.find(x => x.slot_number === slot); if (v) persist(v); }
      return next;
    });
  };

  const onCollegeSelect = (slot, c) => {
    setQueryBySlot(prev => ({ ...prev, [slot]: c.n }));
    updateField(slot, { unitid: c.u, college_name: c.n }, true);
  };

  const addVisit = () => {
    const used = new Set(visits.map(v => v.slot_number));
    let slot = 1;
    while (used.has(slot) && slot <= MAX_VISITS) slot += 1;
    if (slot > MAX_VISITS) return;
    setVisits(prev => [...prev, emptyVisit(slot)]);
    setOrder(prev => ({ ...prev, upcoming: [...prev.upcoming, slot] }));
    setQueryBySlot(prev => ({ ...prev, [slot]: '' }));
  };

  const removeVisit = (visit) => {
    const done = () => {
      setVisits(prev => prev.filter(v => v.slot_number !== visit.slot_number));
      setOrder(prev => ({
        upcoming: prev.upcoming.filter(n => n !== visit.slot_number),
        past: prev.past.filter(n => n !== visit.slot_number),
      }));
      if (onSaved) onSaved();
    };
    if (visit.id) {
      axios.delete(`${API_URL}/athletes/visits/${visit.id}`, cfg()).then(done)
        .catch(err => setError(err.response?.data?.detail || 'Failed to delete visit.'));
    } else {
      done();
    }
  };

  const today = utcTodayIso();
  const bySlot = new Map(visits.map(v => [v.slot_number, v]));
  const upcoming = order.upcoming.map(n => bySlot.get(n)).filter(Boolean);
  const past = order.past.map(n => bySlot.get(n)).filter(Boolean);

  const renderRow = (visit) => (
    <div className="cn-vrow" key={visit.slot_number}>
      <div>
        <div className="cn-visit__college">
          {visit.college_name && <CollegeLogo schoolName={visit.college_name} logoUrl={visit.logo_url} unitid={visit.unitid} size={28} />}
          <CollegePicker
            value={queryBySlot[visit.slot_number] ?? ''}
            onInputChange={q => setQueryBySlot(prev => ({ ...prev, [visit.slot_number]: q }))}
            onSelect={c => onCollegeSelect(visit.slot_number, c)}
            // Pick-only box: leaving it with stray text puts the saved college back
            // rather than showing a name that was never saved.
            onCommitText={() => setQueryBySlot(prev => ({ ...prev, [visit.slot_number]: visit.college_name || '' }))}
            placeholder="Search college…"
            className="cn-vinput"
            rootStyle={{ flex: 1, minWidth: 0 }}
            classNames={{
              popup: 'cn-autocomplete',
              option: 'cn-autocomplete__item',
              optionActive: 'cn-autocomplete__item--active',
              footer: 'cn-autocomplete__hint',
            }}
            renderItem={c => (
              <>
                <div className="cn-autocomplete__name">{c.n}</div>
                <div className="cn-autocomplete__loc">{[c.c, c.s].filter(Boolean).join(', ')}</div>
              </>
            )}
          />
        </div>
        <div className="cn-vrow__rel">{visitRelative(visit, visit.unitid != null && targetUnitids.includes(visit.unitid), today)}</div>
      </div>
      <select className="cn-vinput" aria-label="Type" value={visit.visit_type} onChange={e => updateField(visit.slot_number, { visit_type: e.target.value }, true)}>
        {VISIT_TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
      </select>
      <select className="cn-vinput" aria-label="Format" value={visit.visit_format} onChange={e => updateField(visit.slot_number, { visit_format: e.target.value }, true)}>
        {VISIT_FORMATS.map(f => <option key={f.v} value={f.v}>{f.l}</option>)}
      </select>
      <div className="cn-vrow__dates">
        <input type="date" className="cn-vinput" aria-label="Date" value={visit.visit_date || ''}
          onChange={e => updateField(visit.slot_number, { visit_date: e.target.value })}
          onBlur={() => { const v = visits.find(x => x.slot_number === visit.slot_number); if (v) persist(v); }} />
        {visit.visit_type === 'official' && (
          <input type="date" className="cn-vinput" aria-label="End date" value={visit.visit_end_date || ''}
            onChange={e => updateField(visit.slot_number, { visit_end_date: e.target.value })}
            onBlur={() => { const v = visits.find(x => x.slot_number === visit.slot_number); if (v) persist(v); }} />
        )}
      </div>
      <button type="button" className="cn-vdelete" onClick={() => removeVisit(visit)}>Delete</button>
    </div>
  );

  return (
    <section className="cn-card">
      <div className="cn-card__head">
        <h2 className="cn-card__title">Visits</h2>
        <span className="cn-card__sub">Saves on change</span>
        {visits.length < MAX_VISITS && (
          <button type="button" className="cn-dbtn" style={{ marginLeft: 'auto' }} onClick={addVisit}>+ Add visit</button>
        )}
      </div>

      {error && <div className="cn-card__err"><span className="cn-inline-msg cn-inline-msg--err">{error}</span></div>}

      {loading ? (
        <div className="cn-loading">Loading…</div>
      ) : (
        <div>
          {visits.length === 0 && <div className="cn-empty-line">No visits entered.</div>}
          {upcoming.length > 0 && <div className="cn-group cn-tone-green">Upcoming</div>}
          {upcoming.map(renderRow)}
          {past.length > 0 && <div className="cn-group cn-tone-past">Past</div>}
          {past.map(renderRow)}
        </div>
      )}
    </section>
  );
}
